/**
 * AI Gateway Zustand Store
 *
 * Manages the state for AI Gateway connections and operations.
 */

import { create } from 'zustand';
import type {
  AIGatewayConnectionState,
  AIGatewayCapabilities,
  AIGatewayError,
  PipelineStage,
  RateLimitInfo,
  AIGatewayState,
  AIGatewayActions,
} from '../types/aiGateway';
import { aiGatewayService } from '../services/aiGateway';

const initialState: AIGatewayState = {
  // Connection
  connectionState: 'disconnected',
  sessionId: null,
  capabilities: null,
  error: null,

  // STT State
  isTranscribing: false,
  currentTranscription: '',
  interimTranscription: '',

  // LLM State
  isStreaming: false,
  currentStreamId: null,
  streamedText: '',

  // TTS State
  isSynthesizing: false,
  audioChunks: [],
  isPlaying: false,

  // Pipeline State
  pipelineActive: false,
  pipelineStage: 'idle',
  pipelineTranscription: '',
  pipelineResponse: '',

  // Rate Limiting
  rateLimitInfo: null,
  activeRequests: 0,
};

export const useAIGatewayStore = create<AIGatewayState & AIGatewayActions>((set, _get) => ({
  ...initialState,

  // Connection
  connect: async () => {
    try {
      set({ connectionState: 'connecting', error: null });
      await aiGatewayService.connect();
      set({
        connectionState: 'connected',
        sessionId: null, // Will be set by service callback
        capabilities: aiGatewayService.getCapabilities(),
      });
    } catch (error) {
      set({
        connectionState: 'error',
        error: error as AIGatewayError,
      });
      throw error;
    }
  },

  disconnect: () => {
    aiGatewayService.disconnect();
    set({
      connectionState: 'disconnected',
      sessionId: null,
      capabilities: null,
    });
  },

  setConnectionState: (connectionState: AIGatewayConnectionState) =>
    set({ connectionState }),

  setCapabilities: (capabilities: AIGatewayCapabilities) =>
    set({ capabilities }),

  setSessionId: (sessionId: string) =>
    set({ sessionId }),

  setError: (error: AIGatewayError | null) =>
    set({ error }),

  // STT
  setIsTranscribing: (isTranscribing: boolean) =>
    set({ isTranscribing }),

  setCurrentTranscription: (currentTranscription: string) =>
    set({ currentTranscription }),

  setInterimTranscription: (interimTranscription: string) =>
    set({ interimTranscription }),

  // LLM
  setIsStreaming: (isStreaming: boolean) =>
    set({ isStreaming }),

  setCurrentStreamId: (currentStreamId: string | null) =>
    set({ currentStreamId }),

  appendStreamedText: (text: string) =>
    set((state) => ({ streamedText: state.streamedText + text })),

  setStreamedText: (streamedText: string) =>
    set({ streamedText }),

  // TTS
  setIsSynthesizing: (isSynthesizing: boolean) =>
    set({ isSynthesizing }),

  addAudioChunk: (chunk: string) =>
    set((state) => ({ audioChunks: [...state.audioChunks, chunk] })),

  clearAudioChunks: () =>
    set({ audioChunks: [] }),

  setIsPlaying: (isPlaying: boolean) =>
    set({ isPlaying }),

  // Pipeline
  setPipelineActive: (pipelineActive: boolean) =>
    set({ pipelineActive }),

  setPipelineStage: (pipelineStage: PipelineStage | 'idle') =>
    set({ pipelineStage }),

  setPipelineTranscription: (pipelineTranscription: string) =>
    set({ pipelineTranscription }),

  setPipelineResponse: (pipelineResponse: string) =>
    set({ pipelineResponse }),

  // Rate Limiting
  setRateLimitInfo: (rateLimitInfo: RateLimitInfo | null) =>
    set({ rateLimitInfo }),

  incrementActiveRequests: () =>
    set((state) => ({ activeRequests: state.activeRequests + 1 })),

  decrementActiveRequests: () =>
    set((state) => ({ activeRequests: Math.max(0, state.activeRequests - 1) })),

  // Reset
  reset: () => set(initialState),
}));

// Subscribe to service events
aiGatewayService.onConnectionChange((state) => {
  useAIGatewayStore.getState().setConnectionState(state);
});

aiGatewayService.onCapabilities((capabilities) => {
  useAIGatewayStore.getState().setCapabilities(capabilities);
});

aiGatewayService.onError((error) => {
  useAIGatewayStore.getState().setError(error);
});
